import { useEffect, useState, useCallback } from 'react';

import { useCaseStatisticsByUserAndCase } from '@/hooks/queries/useCaseStatistics';
import { useCertificationProgress } from '@/hooks/queries/useCertificationProgress';
import { captureSentry } from '@/utils/sentryHelper';

import { CertificationStatus } from '../types';

/**
 * Hook to fetch and monitor certification status for a specific case.
 *
 * Certification Access Requirements:
 * 1. Case must be completed (all slides visited)
 * 2. User must have less than 3 attempts
 * 3. User must not have already passed
 *
 * @param caseId - The case ID to check certification status for
 * @param userId - The current user ID
 * @returns {Object} status - Certification status object
 * @returns {boolean} status.canTakeCertification - Whether user can start/retry certification
 * @returns {number} status.attemptsUsed - Number of attempts already used
 * @returns {number} status.maxAttempts - Maximum allowed attempts (3)
 * @returns {number|undefined} status.bestScore - Best certification score achieved
 * @returns {boolean} status.passed - Whether user has passed the certification
 * @returns {boolean} status.caseCompleted - Whether the case is completed
 * @returns {number|undefined} status.caseScore - Current case score percentage
 * @returns {boolean} loading - Whether data is being loaded
 */
export const useCertificationStatus = (
  caseId: string,
  userId: string | null,
  masterclassId: string | null
) => {
  const [status, setStatus] = useState<CertificationStatus>({
    canTakeCertification: false,
    attemptsUsed: 0,
    maxAttempts: 3,
    passed: false,
    caseCompleted: false,
  });
  const [loading, setLoading] = useState(true);

  // Apollo hooks for fetching data
  const { data: statsData, isLoading: statsLoading } = useCaseStatisticsByUserAndCase(
    userId,
    caseId,
    masterclassId
  );
  const { data: attempts, isLoading: attemptsLoading } = useCertificationProgress(
    userId,
    caseId,
    masterclassId
  );

  // Track if Apollo queries are still loading
  const isDataLoading = statsLoading || attemptsLoading;

  const fetchStatus = useCallback(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Wait until Apollo queries have finished loading
    if (isDataLoading) {
      setLoading(true);
      return;
    }

    try {
      // Check completion count from statistics (at least 1 completion required)
      const caseCompleted = (statsData?.completions || 0) > 0;

      // Filter only completed attempts
      const completedAttempts = attempts?.filter((att) => att.completedAt !== null) || [];

      const attemptsUsed = completedAttempts.length;
      const bestScore =
        completedAttempts.reduce((max, att) => Math.max(max, att.score || 0), 0) || undefined;
      const passed = completedAttempts.some((att) => att.passed) || false;
      const canTakeCertification = caseCompleted && attemptsUsed < 3 && !passed;

      setStatus({
        canTakeCertification,
        attemptsUsed,
        maxAttempts: 3,
        bestScore,
        passed,
        caseCompleted,
      });
    } catch (error) {
      console.error('Error fetching certification status:', error);
      captureSentry({
        title: 'Failed to fetch certification status',
        error,
        detail: { caseId, userId },
      });
    } finally {
      setLoading(false);
    }
  }, [userId, caseId, masterclassId, statsData, attempts, isDataLoading]);

  // Re-run when data changes from Apollo queries
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Listen for certification updates via custom events
  useEffect(() => {
    const handleCertificationUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.caseId === caseId) {
        void fetchStatus();
      }
    };

    window.addEventListener('certification-updated', handleCertificationUpdate);

    return () => {
      window.removeEventListener('certification-updated', handleCertificationUpdate);
    };
  }, [caseId, fetchStatus]);

  return { status, loading, refetch: fetchStatus };
};
